/* House of Coda — behaviour for the shared components.
   Deliberately dependency-free and idempotent: every initialiser can run
   again after a section re-render in the theme editor. */
(function () {
  'use strict';

  /* ---- lazy background images ----
     Nearly every picture in our sections is a CSS background, not an <img>,
     so the browser's own loading="lazy" never applied to any of them: all of
     them downloaded the moment the page opened, whether anyone scrolled that
     far or not. Measured on the build theme, that was about 1MB of images
     per page, more than 95% of it below the fold, and most of it swatches.

     Markup now writes data-hoc-bg="url" instead of an inline
     background-image, and this sets the real background when the element
     comes within 600px of the screen. Anything above the fold is inside that
     margin on the first check, so it still paints immediately.

     Keep the hero and the product page's main image OUT of this. They are
     the largest thing on first paint and must never wait for a script.

     No IntersectionObserver (very old browsers) = load everything, i.e. the
     old behaviour. A slower page, never a broken one. */
  var lazyIO = null;
  function showBg(el) {
    /* data-hoc-bg-m (a mobile-width rendition) is optional -- only
       snippets/hoc-product-card sets it today. Everything else that uses
       this pattern (swatches, palette, strip) carries a single data-hoc-bg
       and behaves exactly as before. */
    var mobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    var u = (mobile && el.getAttribute('data-hoc-bg-m')) || el.getAttribute('data-hoc-bg');
    if (!u) return;
    el.style.backgroundImage = 'url(' + u + ')';
    el.removeAttribute('data-hoc-bg');
    el.removeAttribute('data-hoc-bg-m');
  }
  function initLazyBg(root) {
    var els = (root || document).querySelectorAll('[data-hoc-bg]');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) { els.forEach(showBg); return; }
    if (!lazyIO) {
      lazyIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { showBg(e.target); lazyIO.unobserve(e.target); }
        });
      }, { rootMargin: '600px 0px' });
    }
    els.forEach(function (el) { lazyIO.observe(el); });
  }
  window.hocLazyBg = initLazyBg;

  /* ---- cover swatches: swap a product image from real variant media ---- */
  function initCovers(root) {
    root.querySelectorAll('[data-hoc-covers]').forEach(function (group) {
      if (group.dataset.hocReady) return;
      group.dataset.hocReady = '1';

      var target = group.dataset.target;
      var img = document.getElementById('hoc-im-' + target);
      var label = document.getElementById('hoc-cov-' + target);
      var dots = group.querySelectorAll('.hoc-covers__dot');

      function paint(dot) {
        if (!img || !dot.dataset.img) return;
        /* a hover beats a pending lazy load — drop it so it cannot land late
           and put the old cover back over the one just chosen. Clears both
           tiers: the cover swap is a single fixed width regardless of
           viewport (see hoc-cover-swatches.liquid), so a leftover
           data-hoc-bg-m from the card's own responsive hero must not win
           over it if the lazy observer fires afterwards. */
        img.removeAttribute('data-hoc-bg');
        img.removeAttribute('data-hoc-bg-m');
        img.style.backgroundImage = 'url(' + dot.dataset.img + ')';
      }

      function select(dot) {
        if (!img || dot.getAttribute('aria-pressed') === 'true') return;
        dots.forEach(function (d) { d.setAttribute('aria-pressed', 'false'); });
        dot.setAttribute('aria-pressed', 'true');
        var next = new Image();
        next.src = dot.dataset.img;
        img.classList.add('is-loading');
        window.setTimeout(function () {
          paint(dot);
          img.classList.remove('is-loading');
        }, 110);
        if (label) label.textContent = dot.dataset.name || '';
      }

      dots.forEach(function (dot) {
        dot.addEventListener('mouseenter', function () { select(dot); });
        dot.addEventListener('focus', function () { select(dot); });
        dot.addEventListener('click', function (e) { e.preventDefault(); select(dot); });
      });

      /* Open on whichever dot the snippet pressed, so a row of collections
         can each start on a different cover. The section does not have to
         work out that image itself. */
      var initial = null;
      dots.forEach(function (d) {
        if (!initial && d.getAttribute('aria-pressed') === 'true') initial = d;
      });
      if (!initial && dots.length) initial = dots[0];
      if (initial) {
        if (label) label.textContent = initial.dataset.name || '';
        /* If the card's image has not been fetched yet, swap WHICH image it
           will fetch rather than painting now. Painting here used to load
           two pictures per card on every page view — the product's featured
           image from the markup, then this cover on top of it — and do it
           eagerly, far below the fold. Now each card loads exactly one, and
           only when it is about to be seen. */
        if (img && img.hasAttribute('data-hoc-bg') && initial.dataset.img) {
          img.setAttribute('data-hoc-bg', initial.dataset.img);
          /* the rotated-cover swap is a single fixed width, same as above —
             drop any mobile-tier hint left from the card's default hero so
             the lazy observer can't resolve it to the wrong image later */
          img.removeAttribute('data-hoc-bg-m');
        } else {
          paint(initial);
        }
      }
    });
  }

  /* ---- hero before/after reveal ----
     Binds only when the section rendered two frames; with one image the
     stage carries no data-hoc-reveal and this does nothing, which is how the
     hero degrades to a plain photograph before the second frame is shot.

     The handle is a real button, so it also works from the keyboard: arrows
     move it 4% a press, shift+arrow 12%, Home and End go to either end. A
     drag-only control is unusable without a mouse. ---- */
  function initReveal(root) {
    root.querySelectorAll('[data-hoc-reveal]').forEach(function (stage) {
      if (stage.dataset.hocReady) return;
      stage.dataset.hocReady = '1';

      var handle = stage.querySelector('.hoc-hr__hnd');
      var MIN = 4, MAX = 96;
      var pos = parseFloat(stage.parentElement.style.getPropertyValue('--x')) || 62;
      var dragging = false;

      function apply(p) {
        pos = Math.max(MIN, Math.min(MAX, p));
        stage.parentElement.style.setProperty('--x', pos + '%');
        if (handle) {
          handle.setAttribute('aria-valuenow', Math.round(pos));
          handle.setAttribute('aria-valuetext', Math.round(pos) + '% ' + (handle.dataset.left || ''));
        }
      }
      function fromX(clientX) {
        var r = stage.getBoundingClientRect();
        if (!r.width) return;
        apply((clientX - r.left) / r.width * 100);
      }

      function down(e) {
        dragging = true;
        fromX((e.touches ? e.touches[0] : e).clientX);
        if (e.cancelable) e.preventDefault();
      }
      function move(e) {
        if (!dragging) return;
        fromX((e.touches ? e.touches[0] : e).clientX);
      }
      function up() { dragging = false; }

      stage.addEventListener('mousedown', down);
      stage.addEventListener('touchstart', down, { passive: false });
      window.addEventListener('mousemove', move);
      window.addEventListener('touchmove', move, { passive: true });
      window.addEventListener('mouseup', up);
      window.addEventListener('touchend', up);

      if (handle) {
        handle.addEventListener('keydown', function (e) {
          var step = e.shiftKey ? 12 : 4;
          var next = null;
          if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = pos - step;
          else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = pos + step;
          else if (e.key === 'Home') next = MIN;
          else if (e.key === 'End') next = MAX;
          if (next === null) return;
          e.preventDefault();
          apply(next);
        });
        /* clicking the handle itself should not also register as a drag jump */
        handle.addEventListener('click', function (e) { e.preventDefault(); });
      }

      apply(pos);
    });
  }

  /* ---- the fabric drawer ----
     The CARDS are content and come from the server, rendered by the section
     from real fabric records. Filtering hides cells; it never builds a card.
     That is what keeps the grid styleable, lintable and readable by a screen
     reader — a grid assembled from a JS template is none of those things.

     One job used to belong here only because Liquid couldn't do it; it no
     longer does:

     PAGING is not this file's problem any more. The section used to render
     only the first 50 fabric records (Liquid's paginate defaulted to
     by: 50) and fill() below patched over that by asking the Section
     Rendering API for the rest, the first time the drawer opened. That
     fetch never actually worked — confirmed directly, not assumed: a
     page=2 or page=3 request came back byte-identical to page 1, every
     time, because a `paginate` wrapping a metaobjects connection does not
     honour a page= override requested this way. The real fix lives in
     sections/hoc-fabric-drawer.liquid, which now renders with by: 250
     (Liquid's own ceiling for the tag), so the whole catalog — 112 named
     records today — lands on page one in a single pass. fill() is kept
     only as a loud console.warn for if/when the catalog actually grows
     past 250 named fabric records; see that file's top comment for what
     to do at that point (it is not "raise `by` again" — 250 is the tag's
     own ceiling).

     THE CHIPS. The colour and care rows are built from the cards already
     in the DOM, now that every card arrives together on page one. They
     use the same classes the section would have written. Grade is not
     built here — its row is built by the section's own inline script, from
     the real product's Grade option, so it is right on the first paint.
     Once built, though, its chips are bound by the same generic filter
     logic below as every other facet — grade is a plain multi-select
     OR-filter here, not a special case.

     A "line" facet (built from the same fabric_leather.quality field cards
     still carry as data-line) used to render here too; it was removed for
     being too much information in the drawer. Adding a new metadata facet
     later (e.g. fabric type) is one entry in DW_FACETS, one entry each in
     `picked` and DW_FACET_ATTR below, and one term in chosen() — the same
     shape "line" had. ---- */
  var DW_FACETS = [
    { key: 'fam',  label: 'colour' },
    { key: 'care', label: 'care', text: 'careLabel' }
  ];

  function initDrawers(root) {
    root.querySelectorAll('[data-hoc-drawer]').forEach(function (dw) {
      if (dw.dataset.hocReady) return;
      dw.dataset.hocReady = '1';

      var panel = dw.querySelector('.hoc-dw__panel');
      var grid = dw.querySelector('[data-hoc-dw-grid]');
      var facetsEl = dw.querySelector('[data-hoc-dw-facets]');
      var countEl = dw.querySelector('[data-hoc-dw-count]');
      var clearEl = dw.querySelector('[data-hoc-dw-clear]');
      var emptyEl = dw.querySelector('[data-hoc-dw-empty]');
      var pages = parseInt(dw.dataset.pages || '1', 10) || 1;
      var opener = null;
      var filled = false;

      var cells = [];
      var chips = [];
      var total = 0;

      /* facet -> list of chosen values; empty means "no opinion" */
      var picked = { grade: [], fam: [], care: [] };

      /* Every facet is an OR-filter over whatever the shopper has checked,
         zero checked meaning "no opinion" -- including grade, which used to
         be a forced single-select synced to the real product variant and is
         now an ordinary chip row like colour/care (see
         sections/hoc-fabric-drawer.liquid, which builds its buttons).
         Grade's chips carry the card's *normalized* grade
         (data-hoc-fabric-grade: "Grade B" / "grade b" / "B" all read "B"),
         not the raw data-grade a card also carries for display, so this maps
         each facet key to the attribute it actually matches against. */
      var DW_FACET_ATTR = { grade: 'hocFabricGrade', fam: 'fam', care: 'care' };

      function matches(cell) {
        for (var key in picked) {
          var want = picked[key];
          if (!want.length) continue;
          var attr = DW_FACET_ATTR[key] || key;
          if (want.indexOf(cell.dataset[attr] || '') === -1) return false;
        }
        return true;
      }

      function chosen() {
        return picked.grade.length + picked.fam.length + picked.care.length;
      }

      function apply() {
        var n = 0;
        cells.forEach(function (cell) {
          var ok = matches(cell);
          cell.hidden = !ok;
          if (ok) n++;
        });
        if (countEl) countEl.textContent = n === total ? total + ' fabrics' : n + ' of ' + total + ' fabrics';
        if (clearEl) clearEl.hidden = chosen() === 0;
        if (emptyEl) emptyEl.hidden = n !== 0;
      }

      function bindChip(chip) {
        chip.addEventListener('click', function () {
          var key = chip.dataset.hocDwFilter;
          var val = chip.dataset.value || '';
          var list = picked[key];
          if (!list) return;
          var at = list.indexOf(val);
          if (at === -1) { list.push(val); } else { list.splice(at, 1); }
          chip.setAttribute('aria-pressed', at === -1 ? 'true' : 'false');
          apply();
        });
      }

      function bindPick(cell) {
        var card = cell.querySelector('[data-hoc-dw-pick]');
        if (!card || card.dataset.hocBound) return;
        card.dataset.hocBound = '1';
        card.addEventListener('click', function () {
          cells.forEach(function (c) {
            var b = c.querySelector('[data-hoc-dw-pick]');
            if (b) b.setAttribute('aria-pressed', 'false');
          });
          card.setAttribute('aria-pressed', 'true');

          var pick = {
            name: card.dataset.name || '',
            grade: card.dataset.grade || '',
            note: card.dataset.note || '',
            image: card.dataset.image || '',
            handle: card.dataset.handle || ''
          };

          /* The drawer announces; it does not reach into the product form.
             Anything that wants the choice listens for hoc:fabric, and the
             four hooks below cover the ordinary cases with no page-specific
             JS — including the hidden line item property that puts the
             fabric on the order. */
          document.querySelectorAll('[data-hoc-fabric-name]').forEach(function (el) { el.textContent = pick.name; });
          document.querySelectorAll('[data-hoc-fabric-note]').forEach(function (el) { el.textContent = pick.note; });
          document.querySelectorAll('[data-hoc-fabric-input]').forEach(function (el) { el.value = pick.name; });
          document.querySelectorAll('[data-hoc-fabric-image]').forEach(function (el) {
            if (pick.image) el.style.backgroundImage = 'url(' + pick.image + ')';
          });

          /* The product page's material inset (sections/hoc-product.liquid):
             a swatch tile that sits on the hero photo once a fabric is
             picked. Its own hook, not a fifth consumer of data-hoc-fabric-image
             above -- it also needs to hide itself again for a pick with no
             image, which that hook's existing use was never asked to do. */
          document.querySelectorAll('[data-hoc-fabric-swatch]').forEach(function (wrap) {
            wrap.hidden = !pick.image;
            var tile = wrap.querySelector('[data-hoc-fabric-swatch-img]');
            if (pick.image && tile) tile.style.backgroundImage = 'url(' + pick.image + ')';
          });

          document.dispatchEvent(new CustomEvent('hoc:fabric', { detail: pick }));
          close();
        });
      }

      /* one row per facet, in the same classes the section writes */
      function buildFacets() {
        if (!facetsEl) return;
        facetsEl.querySelectorAll('[data-hoc-dw-built]').forEach(function (r) { r.remove(); });
        /* drop the chips that just went with them, keep the grade row's */
        chips = chips.filter(function (c) { return c.isConnected; });

        DW_FACETS.forEach(function (facet) {
          var seen = {};
          cells.forEach(function (cell) {
            var v = cell.dataset[facet.key];
            if (v) seen[v] = cell.dataset[facet.text || facet.key] || v;
          });
          var values = Object.keys(seen).sort();
          if (!values.length) return;

          var row = document.createElement('div');
          row.className = 'hoc-dw__frow';
          row.setAttribute('data-hoc-dw-built', '');

          var label = document.createElement('span');
          label.className = 'hoc-dw__flbl hoc-lbl hoc-lbl--nano hoc-lbl--lower';
          label.textContent = facet.label;
          row.appendChild(label);

          values.forEach(function (v) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'hoc-dw__f hoc-lbl hoc-lbl--nano hoc-lbl--lower hoc-lbl--inherit';
            b.setAttribute('aria-pressed', 'false');
            b.setAttribute('data-hoc-dw-filter', facet.key);
            b.setAttribute('data-value', v);
            b.textContent = seen[v];
            row.appendChild(b);
            chips.push(b);
            bindChip(b);
          });

          facetsEl.appendChild(row);
        });
      }

      function refresh() {
        cells = Array.prototype.slice.call(dw.querySelectorAll('[data-hoc-dw-cell]'));
        total = cells.length;
        cells.forEach(bindPick);
        buildFacets();
        apply();
      }

      /* ---- first-open guard: catalog growth past by:250 ---- */
      function fill() {
        if (filled) return;
        filled = true;

        /* Paging is not this file's problem any more — see the comment at
           the top of initDrawers() and the top of
           sections/hoc-fabric-drawer.liquid. That file now renders with
           by: 250, Liquid's own ceiling for the paginate tag, so the whole
           catalog already sits in the DOM before this ever runs; dw's own
           dataset.pages is "1" and there is nothing left to fetch. The
           Section Rendering API fetch this function used to make here is
           gone, not merely unused: it was confirmed, by fetching page=2 and
           page=3 directly and comparing their output, to come back
           byte-identical to page 1 every time, so it was never actually
           advancing the catalog — only adding latency and a false sense
           that paging worked.

           This stays only as a loud warning for the day the catalog
           genuinely outgrows by:250 (more than 250 named fabric records).
           At that point `by` cannot simply go higher — 250 is Liquid's own
           hard ceiling for the tag — so whoever sees this warning should
           read the "WHY by:250" comment at the top of
           sections/hoc-fabric-drawer.liquid before changing anything. */
        if (pages > 1) {
          window.console && console.warn(
            'hoc-fabric-drawer: dataset.pages is ' + pages + ', meaning the ' +
            'fabric catalog has grown past what by:250 covers in ' +
            'sections/hoc-fabric-drawer.liquid. Shoppers are only seeing the ' +
            'first 250 named fabric records. See that file\'s "WHY by:250" ' +
            'comment before changing `by` — 250 is Liquid\'s own ceiling for ' +
            'the paginate tag, so raising it again is not the fix.'
          );
        }
      }

      /* ---- open and close ---- */
      function focusables() {
        return Array.prototype.slice.call(
          panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
        ).filter(function (el) { return el.offsetParent !== null; });
      }

      function open(from) {
        opener = from || null;
        fill();
        dw.classList.add('is-open');
        document.documentElement.style.overflow = 'hidden';
        var f = focusables();
        if (f.length) f[0].focus();
      }

      function close() {
        dw.classList.remove('is-open');
        document.documentElement.style.overflow = '';
        if (opener && opener.focus) opener.focus();
        opener = null;
      }

      dw.querySelectorAll('[data-hoc-dw-close]').forEach(function (b) {
        b.addEventListener('click', close);
      });

      /* Esc closes; Tab is kept inside the panel while it is open, because a
         modal you can tab out of is a modal only for people using a mouse. */
      document.addEventListener('keydown', function (e) {
        if (!dw.classList.contains('is-open')) return;
        if (e.key === 'Escape') { e.preventDefault(); close(); return; }
        if (e.key !== 'Tab') return;
        var f = focusables();
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });

      /* Any element anywhere on the page may open this drawer, which is what
         lets the product page be built separately from it. */
      document.querySelectorAll('[data-hoc-dw-open]').forEach(function (btn) {
        var want = btn.getAttribute('data-hoc-dw-open');
        if (want && want !== dw.id) return;
        btn.addEventListener('click', function (e) { e.preventDefault(); open(btn); });
      });

      if (clearEl) {
        clearEl.addEventListener('click', function () {
          Object.keys(picked).forEach(function (k) { picked[k].length = 0; });
          chips.forEach(function (c) { c.setAttribute('aria-pressed', 'false'); });
          apply();
        });
      }

      dw.querySelectorAll('[data-hoc-dw-filter]').forEach(function (c) { chips.push(c); bindChip(c); });

      dw.hocOpen = open;
      dw.hocClose = close;
      refresh();
    });
  }

  /* ---- accordion ---- */
  function initAccordions(root) {
    root.querySelectorAll('[data-hoc-acc]').forEach(function (acc) {
      if (acc.dataset.hocReady) return;
      acc.dataset.hocReady = '1';
      var head = acc.querySelector('.hoc-acc__h');
      if (!head) return;
      head.addEventListener('click', function () {
        var open = acc.classList.toggle('is-open');
        head.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  /* ---- sticky commit bar ---- */
  function initBars(root) {
    root.querySelectorAll('[data-hoc-bar]').forEach(function (bar) {
      if (bar.dataset.hocReady) return;
      bar.dataset.hocReady = '1';
      var trigger = document.querySelector(bar.dataset.trigger);
      if (!trigger) return;
      bar.hidden = false;
      var onScroll = function () {
        var past = trigger.getBoundingClientRect().top < window.innerHeight * 0.35;
        var footer = document.querySelector('[data-hoc-bar-stop]');
        var atEnd = footer ? footer.getBoundingClientRect().top < window.innerHeight - 60 : false;
        bar.classList.toggle('is-on', past && !atEnd);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    });
  }

  function init(root) {
    var scope = root || document;
    initCovers(scope);
    initLazyBg(scope);
    initReveal(scope);
    initDrawers(scope);
    initAccordions(scope);
    initBars(scope);
  }

  document.addEventListener('DOMContentLoaded', function () { init(document); });
  /* theme editor re-renders a section without a page load */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
